import { keyed } from 'lit/directives/keyed.js';
import { engineFieldAttributes } from '../../sus-entry-form/registration-form/client/fields/engine-field-attributes.js';
import { FileInput } from '@adornis/file-utils/client/file-input.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { fileStorage } from '../utils/indexeddb-file-storage.js';

interface IndexedDBFileInputProps {
  placeholder?: string;
  value: Maybe<string>;
  disabled?: boolean;
  onValueChange?: (value: string | null) => void;
  onFilePicked?: (fileId: string | null) => void;
}

export const SgIndexedDBFileInput = stateful((props: IndexedDBFileInputProps) => {
  const [name, setName] = useState('');
  const [fieldID] = useState(`sus-file-${crypto.randomUUID()}`, []);
  const [resetKey, setResetKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setName('');

    setError('');
    if (props.value)
      void fileStorage
        .getFile(props.value)
        .then(file => {
          if (!active) return;
          if (file) {
            setName(file.name);
          } else setError('Die gespeicherte Datei fehlt auf diesem Gerät. Bitte wählen Sie sie erneut aus.');
        })
        .catch(() => {
          if (active) setError('Die Datei konnte nicht geladen werden. Bitte wählen Sie sie erneut aus.');
        });
    return () => {
      active = false;
    };
  }, [props.value]);
  const update = (value: string | null) => {
    props.onValueChange?.(value);
    props.onFilePicked?.(value);
  };
  const select = async (files: File[]) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const file = files[0];
      if (file) {
        const id = await fileStorage.storeFile(file);

        setName(file.name);
        update(id);
      } else {
        if (props.value) await fileStorage.deleteFile(props.value);

        setName('');
        update(null);
      }
    } catch {
      setError('Die Dateiauswahl konnte nicht auf diesem Gerät gespeichert werden. Bitte versuchen Sie es erneut.');
      setResetKey(resetKey + 1);
    } finally {
      setBusy(false);
    }
  };
  return html`<div
    class="flex min-w-0 w-full flex-col gap-2"
    ${engineFieldAttributes({ id: fieldID, label: props.placeholder ?? 'Datei auswählen', error })}
  >
    <p class="text-sm font-semibold text-base-content/70">${props.placeholder ?? 'Datei auswählen'}</p>
    ${keyed(
      `${props.value ?? ''}:${resetKey}`,
      FileInput({
        placeholder: 'Datei auswählen',
        placeholderMode: 'hide',
        disabled: props.disabled || busy,
        error,
        onFilesChanged: state => {
          void select(state.pendingFiles);
        },
      }),
    )}
    ${busy ? html`<p role="status" class="text-sm text-base-content/70">Datei wird auf diesem Gerät gespeichert …</p>` : nothing}
    ${
      name
        ? html`<div class="flex min-w-0 flex-wrap items-center gap-2">
            <p class="min-w-0 flex-1 basis-40 break-all text-sm">${name}</p>
            <button
              type="button"
              class="btn btn-ghost btn-square shrink-0"
              aria-label=${`${props.placeholder ?? 'Datei'} öffnen`}
              ?disabled=${busy}
              @click=${async () => {
                if (!props.value) return;
                const file = await fileStorage.getFile(props.value).catch(() => null);
                if (!file) {
                  setError('Die Datei konnte nicht geöffnet werden.');
                  return;
                }
                const url = fileStorage.createFileUrl(file);
                window.open(url, '_blank', 'noopener');
                setTimeout(() => URL.revokeObjectURL(url), 60000);
              }}
            >
              <span class="icon-[material-symbols--open-in-new-rounded] text-xl" aria-hidden="true"></span>
            </button>
            <button
              type="button"
              class="btn btn-ghost btn-square shrink-0"
              aria-label=${`${props.placeholder ?? 'Datei'} entfernen`}
              ?disabled=${props.disabled || busy}
              @click=${async () => {
                if (busy) return;
                setBusy(true);
                try {
                  if (props.value) await fileStorage.deleteFile(props.value);
                  update(null);
                  setName('');
                  setError('');
                } catch {
                  setError('Die Datei konnte nicht entfernt werden.');
                } finally {
                  setBusy(false);
                }
              }}
            >
              <span class="icon-[material-symbols--close-rounded] text-xl" aria-hidden="true"></span>
            </button>
          </div>`
        : nothing
    }
  </div>`;
});
